import { Auth } from '@aws-amplify/auth'
import { useTenant } from '@emerald-works-nova/auth'
import { useAuthConfig, useSession } from '@emerald-works/react-auth'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import Cookies from 'universal-cookie'
import { useAlert } from '../../hooks'
import { authErrorSlice } from '../../reducers'
import { MsgAlert } from '../alert'
import { NotAuthenticatedAlert } from './alert'
import NotAuthorized from './not-authorized'

const Authenticate = () => {
  const location = useLocation()
  const tenant = useTenant()
  const authConfig = useAuthConfig()
  const { enqueueAlert } = useAlert()
  const cookies = new Cookies()
  const cookieKey = authConfig.afterAuthLocationCookieName
  const targetURI = `${location.pathname}${location.search}`
  cookies.set(cookieKey, targetURI, { path: '/' })
  const isThereACustomProvider = tenant.loginProviders.find(({ providerType }) => providerType === 'SAML')
  const authError = useSelector(authErrorSlice.selectors.selectAuthError)

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .catch(() => {
        if (tenant?.tenantKey === 'www') {
          enqueueAlert(<NotAuthenticatedAlert />)
        }

        // if there is an auth error, show it and throw it
        if (authError) {
          const decodedErrorMessage = decodeURIComponent(authError.errorMessage).replaceAll('+', ' ').trim()
          const errorMsg = `${authError.message}: ${decodedErrorMessage}`
          enqueueAlert(<MsgAlert title='SSO Sign In Error' msg={errorMsg} severity='error' />)
          throw new Error(errorMsg)
        }

        authConfig.onAuthNeededRedirection({
          Auth,
          federatedSignInParams: { customState: cookieKey },
          location
        })
      })
  }, [authConfig, location, cookieKey, enqueueAlert, tenant, isThereACustomProvider, authError])

  return null
}

const PrivateRoute = ({
  element,
  allowedUserGroups,
  shouldShowStubbedPage = false,
  shouldCheckAuthentication = true,
  ...rest
}) => {
  const { isAuthenticated, session } = useSession()
  const authConfig = useAuthConfig()
  const userGroups = session?.accessToken?.payload?.['cognito:groups']?.join(',') || 'defaultUserGroup'
  const userHasApplicationAccess = userGroups.match(allowedUserGroups || authConfig.allowedUserGroups)?.length > 0

  // return the Authenticate component if not authenticated
  if (!isAuthenticated) {
    return <Authenticate />
  }

  // return user not authorized component if user has no application access
  if (!userHasApplicationAccess) {
    return <NotAuthorized />
  }

  // return page
  return element
}

export default PrivateRoute
